import React from 'react';
import StatisticsBox from '../components/StatisticsBox';
import ReadinessGauge from '../components/ReadinessGauge';
import DistributionChart from '../components/DistributionChart';
import PerformanceChart from '../components/PerformanceChart';
import styles from './VPView.module.css';
import layoutStyles from '../WindowReadinessResults.module.css';

const VPView = ({ data }) => {
  if (!data) return <div className="loading">Loading VP view...</div>;

  const { overallStats, wowComparison, directorPerformance, topLocations, isFirstUpload } = data;

  // Prepare director performance data for chart
  const directorChartData = directorPerformance.map((director) => ({
    label: director.director,
    value: parseFloat(director.completion_percentage),
  }));

  // Prepare top 10 locations data
  const topLocationsChartData = topLocations.map((location) => ({
    label: `${location.training_location_name} (${location.city})`,
    value: parseFloat(location.completion_percentage),
  }));

  // Determine action items based on threshold
  const getActionItems = () => {
    const items = [];
    const completion = parseFloat(overallStats.overall_completion);

    if (isFirstUpload) {
      if (completion >= 80) {
        items.push({ type: 'success', text: 'Maintain compliance across all locations' });
      } else if (completion >= 60) {
        items.push({ type: 'warning', text: 'Focus on locations below 80% completion' });
        items.push({ type: 'info', text: `${overallStats.locations_caution + overallStats.locations_critical} locations need attention` });
      } else {
        items.push({ type: 'critical', text: 'Deploy resources to critical locations immediately' });
        items.push({ type: 'critical', text: `${overallStats.locations_critical} locations are in critical status` });
      }
    } else {
      if (wowComparison.trend === 'up') {
        items.push({ type: 'success', text: `Great progress! Up ${wowComparison.change.toFixed(1)}% from last week` });
        items.push({ type: 'info', text: 'Recognize improvements and share best practices' });
      } else if (wowComparison.trend === 'down') {
        items.push({ type: 'warning', text: `Completion declined by ${Math.abs(wowComparison.change).toFixed(1)}%` });
        items.push({ type: 'warning', text: 'Support declining locations with additional resources' });
      } else {
        items.push({ type: 'info', text: 'Performance holding steady' });
      }

      if (overallStats.locations_critical > 0) {
        items.push({ type: 'critical', text: `${overallStats.locations_critical} locations require immediate intervention` });
      }
    }

    return items;
  };

  const actionItems = getActionItems();

  return (
    <div className={styles.vpView}>
      <StatisticsBox stats={overallStats} wowComparison={wowComparison} />

      <div className={layoutStyles.chartsGrid}>
        <ReadinessGauge
          percentage={parseFloat(overallStats.overall_completion)}
          title="Company-Wide Readiness"
        />
        <DistributionChart
          ready={overallStats.locations_ready}
          caution={overallStats.locations_caution}
          critical={overallStats.locations_critical}
          title="Location Distribution"
        />
      </div>

      <div className={layoutStyles.chartsGrid}>
        <PerformanceChart
          data={directorChartData}
          title="Director Performance"
        />
        <PerformanceChart
          data={topLocationsChartData}
          title="Top 10 Locations Needing Attention"
        />
      </div>

      <div className={layoutStyles.actionItems}>
        <h3>Action Items</h3>
        <ul>
          {actionItems.map((item, index) => (
            <li key={index} className={layoutStyles[`actionItem${item.type.charAt(0).toUpperCase() + item.type.slice(1)}`]}>
              {item.text}
            </li>
          ))}
        </ul>
      </div>

      <div className={layoutStyles.dataTable}>
        <h3>Director Summary</h3>
        <div className={layoutStyles.tableResponsive}>
          <table>
            <thead>
              <tr>
                <th>Director</th>
                <th>Locations</th>
                <th>Total Employees</th>
                <th>Completed</th>
                <th>Completion %</th>
              </tr>
            </thead>
            <tbody>
              {directorPerformance.map((director, index) => (
                <tr key={index} className={layoutStyles[`row${director.completion_percentage >= 80 ? 'Ready' : director.completion_percentage >= 60 ? 'Caution' : 'Critical'}`]}>
                  <td>{director.director}</td>
                  <td>{director.location_count}</td>
                  <td>{director.total_employees.toLocaleString()}</td>
                  <td>{director.employees_completed.toLocaleString()}</td>
                  <td>
                    <span className={layoutStyles.completionBadge}>{director.completion_percentage}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VPView;
